import React, { FunctionComponent, ReactNode, useCallback, useEffect, useState } from 'react';
import { useScreenSize } from '../../hooks/useScreenSize';
import { SanityArticleCategory, SanityCategoryGroup } from '../../types/common';
import Filters from '../Filters';
import { FunnelIcon, InternalLinkIcon } from '../Icons';
import './styles.scss';

type ListProps = {
  singularLabel: string;
  pluralLabel: string;
  items: ReactNode[];
  withoutListPadding?: boolean;
  header?: ReactNode;
  className?: string;
  filter?: {
    isAdvanced?: boolean | null;
    groups?: SanityCategoryGroup[] | null;
    categories?: SanityArticleCategory[] | null;
    filteredCategories: string[];
    filteredGroups?: string[];
    onFilter: (categoryId: string) => void;
    onFilterGroup?: (groupKey: string) => void;
    onClearFilters: () => void;
  }
}

const List: FunctionComponent<ListProps> = ({ className, header, singularLabel, pluralLabel, items, filter, withoutListPadding }) => {
  const listSize = 10;
  const [list, setList] = useState([...items.slice(0, listSize)]);
  const [loadMore, setLoadMore] = useState(false);
  const [hasMore, setHasMore] = useState(items.length > listSize);
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const { isMobile } = useScreenSize();

  function handleLoadMore() {
    setLoadMore(true);
  }

  function handleCloseFilter() {
    setIsFilterOpen(false);
  }

  useEffect(() => {
    setList([...items.slice(0, listSize)]);
    setHasMore(items.length > listSize);
  }, [items]);

  useEffect(() => {
    if (hasMore && loadMore) {
      const currentLength = list.length;
      const nextResults = currentLength < items.length ? items.slice(currentLength, currentLength + listSize) : [];
      setList(prevList => [...prevList, ...nextResults]);
      setLoadMore(false);
    }
  }, [hasMore, loadMore]);

  useEffect(() => {
    const isMore = list.length < items.length;
    setHasMore(isMore);
  }, [list]);

  return (
    <section className={`list ${className ? className : ''}`}>
      <div>
        {isMobile && header && (
          <div className='list-header'>
            {header}
          </div>
        )}
        {!!filter && !!filter.categories && (
          <Filters
            data={filter.isAdvanced ? {
              ...filter,
              isAdvancedFilterOpen: isFilterOpen,
              onCloseAdvancedFilter: handleCloseFilter
            } : filter}
          />
        )}
        <div className='list-container'>
        {!isMobile && header && (
          <div className='list-header'>
            {header}
          </div>
        )}
          <div className='list-quantity'>
            <span>
              {items.length} {items.length === 1 ? singularLabel : pluralLabel}
            </span>
            {isMobile && !!filter && (
              <div className='filter-actions'>

                {filter?.isAdvanced && (
                  <button
                    onClick={() => setIsFilterOpen(true)}
                    data-filters-count={filter.filteredCategories.length}
                    className={`filter-actions-filter ${filter.filteredCategories.length === 0 ? 'void' : ''}`}
                  >
                    Advanced Filters
                    <FunnelIcon />
                  </button>
                )}

                {((filter.filteredCategories && filter.filteredCategories.length > 0) || (filter.filteredGroups && filter.filteredGroups.length > 0)) && (
                  <button className='clear' onClick={filter.onClearFilters}>
                    Clear all filters
                  </button>
                )}
              </div>
            )}
          </div>

          <div className={`list-content ${withoutListPadding ? 'without-padding' : ''}`}>
            {list}
          </div>

          {hasMore && (
            <button
              type='button'
              onClick={handleLoadMore}
              className='primary-button'
            >
              Load more
              <InternalLinkIcon />
            </button>
          )}
        </div>
      </div>
    </section>
  );
}

export default List;
